import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import MenuIcon from '@mui/icons-material/Menu';
import Box from '@mui/material/Box';
import Collapse from '@mui/material/Collapse';
import Divider from '@mui/material/Divider';
import Drawer from '@mui/material/Drawer';
import IconButton from '@mui/material/IconButton';
import Link from '@mui/material/Link';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';
import Skeleton from '@mui/material/Skeleton';
import { useTheme } from '@mui/material/styles';
import PropTypes from 'prop-types';
import { Fragment, useEffect, useState } from 'react';
import { Link as RouterLink, useLocation, useMatch } from 'react-router-dom';

const MobileNavMenu = ({ navigationItems, isLoading = false }) => {
	const { palette } = useTheme();
	const [drawerOpen, setDrawerOpen] = useState(false);
	const [open, setOpen] = useState(null);
	const location = useLocation();
	const isDropdownActive = useMatch(`/:topNav/:page`);

	const handleTopNavItemClick = rootNavItem => {
		if (rootNavItem.isDropdown) {
			if (open === rootNavItem.key) {
				setOpen(null);
			} else {
				setOpen(rootNavItem.key);
			}
		} else {
			setOpen(null);
			setDrawerOpen(false);
		}
	};

	/* checks if non-dropdown menu item is active */
	const isRootPathActive = path => {
		if (path === '/') {
			return location.pathname === '/';
		}
		return location.pathname.startsWith(path);
	};

	/* set open state based on dropdown active state on inital load */
	useEffect(() => {
		if (isDropdownActive) {
			setOpen(isDropdownActive.params.topNav);
		}
	}, []);

	return (
		<>
			<IconButton
				aria-label='Mobile menu'
				aria-haspopup='dialog'
				onClick={() => setDrawerOpen(true)}
				sx={{
					color: palette.text.primary,
					mr: 0.5,
				}}
			>
				<MenuIcon />
			</IconButton>
			<Drawer
				onClose={() => setDrawerOpen(false)}
				open={drawerOpen}
				PaperProps={{
					sx: { width: '66%' },
					'aria-label': 'Mobile navigation menu',
				}}
			>
				{isLoading && (
					<Box role='progressbar' aria-label='Loading'>
						<List>
							<ListItem>
								<Skeleton variant='text' width={200} />
							</ListItem>
							<ListItem>
								<Skeleton variant='text' width={200} />
							</ListItem>
							<ListItem>
								<Skeleton variant='text' width={200} />
							</ListItem>
							<ListItem>
								<Skeleton variant='text' width={200} />
							</ListItem>
							<ListItem>
								<Skeleton variant='text' width={200} />
							</ListItem>
						</List>
					</Box>
				)}
				<List>
					{!isLoading &&
						navigationItems.map(topNavItem => (
							<Fragment key={topNavItem.key}>
								<ListItem key={topNavItem.key} sx={{ p: 0 }}>
									<ListItemButton
										{...(!topNavItem.isDropdown &&
											(topNavItem.isLegacy
												? {
														component: Link,
														href: topNavItem.path,
														target: topNavItem.isExternal
															? '_blank'
															: '_self',
														rel: 'noreferrer',
													}
												: {
														component: RouterLink,
														to: topNavItem.path,
													}))}
										selected={
											isRootPathActive(topNavItem.path) ||
											isDropdownActive?.params.topNav ===
												topNavItem.key
										}
										onClick={() =>
											handleTopNavItemClick(topNavItem)
										}
									>
										<ListItemText
											primary={topNavItem.label}
										/>
										{topNavItem.isDropdown &&
											(topNavItem.key === open ? (
												<ExpandMoreIcon
													onClick={() =>
														setOpen(null)
													}
												/>
											) : (
												<KeyboardArrowRightIcon
													onClick={() =>
														setOpen(topNavItem.key)
													}
												/>
											))}
									</ListItemButton>
								</ListItem>
								{topNavItem.isDropdown && (
									<Collapse
										in={topNavItem.key === open}
										timeout='auto'
										unmountOnExit
									>
										<List component='div'>
											{topNavItem.menuItems.map(
												menuItem => (
													<ListItemButton
														key={menuItem.key}
														{...(menuItem.isLegacy
															? {
																	component:
																		Link,
																	href: menuItem.path,
																	target: '_self',
																	rel: 'noreferrer',
																}
															: {
																	component:
																		RouterLink,
																	to: menuItem.path,
																	onClick:
																		() =>
																			setDrawerOpen(
																				false
																			),
																})}
														sx={{
															color: palette.text
																.primary,
															pl: 4,
														}}
														selected={
															isDropdownActive?.pathnameBase ===
															menuItem.path
														}
													>
														<ListItemText
															primary={
																menuItem.label
															}
														/>
													</ListItemButton>
												)
											)}
										</List>
									</Collapse>
								)}
							</Fragment>
						))}
				</List>
				<Divider />
			</Drawer>
		</>
	);
};

MobileNavMenu.propTypes = {
	navigationItems: PropTypes.array.isRequired,
	isLoading: PropTypes.bool,
};

export default MobileNavMenu;
